import { useSortable } from "@dnd-kit/sortable";
import { KebabIcon } from "assets/icons";
import React from "react";
import styled, { css } from "styled-components";
import { CSS } from "@dnd-kit/utilities";

type LabelColorVariant = "blue" | "red" | "grey";

interface LabelProps extends React.HTMLAttributes<HTMLDivElement> {
  onClickEdit?: () => void;
  variant: LabelColorVariant;
  id: string;
}

const Tag = ({ children, onClickEdit, id, ...rest }: LabelProps) => {
  const { setNodeRef, attributes, listeners, transform, transition } =
    useSortable({ id, handle: true } as Parameters<typeof useSortable>[0]);

  return (
    <LabelStyle
      {...rest}
      ref={setNodeRef}
      {...attributes}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        cursor: "grab",
      }}
    >
      <div {...listeners}>{children}</div>
      {onClickEdit && (
        <EditButtonStyle onClick={onClickEdit}>
          <KebabIcon />
        </EditButtonStyle>
      )}
    </LabelStyle>
  );
};

const LabelStyle = styled.div<Omit<LabelProps, "id">>`
  display: inline-flex;
  justify-content: center;
  align-items: center;
  gap: 0.2rem;
  flex-shrink: 0;

  padding: 0.4rem 1rem;
  border-radius: 0.4rem;
  touch-action: none;

  ${({ theme }) => theme.typography.body2.regular};
  ${({ variant }) => cssLabelColor(variant)};

  ${({ theme }) => css`
    ${theme.media.small} {
      padding: 0.2rem 0.8rem;
      ${theme.typography.caption1.regular};
    }
  `}
  background-color: #dadfe5;
`;

const EditButtonStyle = styled.div`
  display: inline-flex;
  justify-content: center;
  align-items: center;

  cursor: pointer;
`;

const cssLabelColor = (variant: LabelColorVariant) => {
  switch (variant) {
    case "blue":
      return css`
        background-color: ${({ theme }) => theme.color.skyblue[100]};
        color: ${({ theme }) => theme.color.skyblue[700]};
      `;
    case "red":
      return css`
        background-color: ${({ theme }) => theme.color.brick[100]};
        color: ${({ theme }) => theme.color.brick[700]};
      `;

    case "grey":
      return css`
        background-color: ${({ theme }) => theme.color.grey[300]};
        color: ${({ theme }) => theme.color.grey[800]};
      `;
  }
};

export default Tag;
